import { Badge, Button, Card, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Globe, Mic, Pencil, Plus, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  deleteSermon,
  listSermons,
  publishSermon,
  unpublishSermon,
  type Sermon,
} from '../../lib/content-api'
import { statusBadgeVariant } from './fields'

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function SermonsAdmin() {
  const qc = useQueryClient()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['content', 'sermons'],
    queryFn: listSermons,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['content', 'sermons'] })

  const publishMut = useMutation({
    mutationFn: (s: Sermon) =>
      s.status === 'published' ? unpublishSermon(s.id) : publishSermon(s.id),
    onSuccess: invalidate,
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteSermon(id),
    onSuccess: invalidate,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sermons"
        description="Sermon library with audio / video and optional series."
        actions={
          <Button asChild>
            <Link to="/app/content/sermons/new">
              <Plus />
              New sermon
            </Link>
          </Button>
        }
      />

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading sermons…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load sermons.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running?'}
            </p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-center">
            <div className="grid size-12 place-items-center rounded-full bg-card text-secondary">
              <Mic className="size-6" />
            </div>
            <p className="max-w-sm text-sm text-muted">
              No sermons yet. Add your first sermon with its audio or video link.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Title</th>
                  <th className="px-5 py-3 font-semibold">Series</th>
                  <th className="px-5 py-3 font-semibold">Speaker</th>
                  <th className="px-5 py-3 font-semibold">Preached</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((s) => (
                  <tr
                    key={s.id}
                    className="border-b border-border-soft transition-colors last:border-0 hover:bg-card-hover"
                  >
                    <td className="px-5 py-3">
                      <Link
                        to={`/app/content/sermons/${s.id}`}
                        className="font-medium hover:text-secondary"
                      >
                        {s.title}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-muted">{s.series ?? '—'}</td>
                    <td className="px-5 py-3 text-muted">{s.speaker ?? '—'}</td>
                    <td className="px-5 py-3 text-muted">{formatDate(s.preachedAt)}</td>
                    <td className="px-5 py-3">
                      <Badge variant={statusBadgeVariant(s.status)} className="capitalize">
                        {s.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => publishMut.mutate(s)}
                          disabled={publishMut.isPending}
                        >
                          <Globe />
                          {s.status === 'published' ? 'Unpublish' : 'Publish'}
                        </Button>
                        <Button variant="ghost" size="icon-sm" asChild aria-label="Edit sermon">
                          <Link to={`/app/content/sermons/${s.id}`}>
                            <Pencil />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Delete sermon"
                          onClick={() => {
                            if (confirm(`Delete “${s.title}”? This cannot be undone.`))
                              deleteMut.mutate(s.id)
                          }}
                          disabled={deleteMut.isPending}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
